import axios from 'axios'
import { IsAuthorized } from 'src/authentication/IsAuthorized'
import { getJWT } from 'src/helper/localStore/getJWT'

class SupplierConciliationServices {
  async processFile(
    file: File,
    supplierId: number,
    currencyId: number,
    conciliationType?: string | null,
    clientId?: number | null,
    name?: string | null
  ): Promise<any> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('supplier_id', String(supplierId))
      formData.append('currency_id', String(currencyId))
      if (conciliationType) {
        formData.append('conciliation_type', String(conciliationType))
      }
      if (clientId !== undefined && clientId !== null) {
        formData.append('client_id', String(clientId))
      }
      if (name && name.trim()) {
        formData.append('name', name.trim())
      }

      const res = await axios.post(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/process`,
        formData,
        {
          headers: {
            'authentication-token': getJWT()!,
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async getConciliationById(id: number): Promise<any> {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/${id}`,
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async updateConciliation(id: number | string, payload: any): Promise<any> {
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/${id}`,
        payload,
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  /**
   * Obtiene los ítems de una conciliación enviando parámetros de paginado, ordenamiento y filtro.
   * @param id ID de la conciliación
   * @param params Objeto con { skip, limit, ordering, status, code, is_paid, is_billed }
   */
  async getConciliationItems(
    id: number | string,
    params: {
      skip?: number
      limit?: number
      ordering?: string
      status?: string
      code?: string
      is_paid?: boolean
      is_billed?: boolean
      [key: string]: any
    } = {}
  ): Promise<any> {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/${id}/items`,
        {
          headers: { 'authentication-token': getJWT()! },
          params,
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async reconcileItem(itemId: number | string, fromAdjustment: boolean = false): Promise<any> {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/item/${itemId}/reconcile`,
        {},
        {
          headers: { 'authentication-token': getJWT()! },
          params: { from_adjustment: fromAdjustment },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async getAssociatedItems(itemId: number | string): Promise<any> {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/item/${itemId}/associated-items`,
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async getBulkAssociatedItems(itemIds: (number | string)[]): Promise<any> {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/items/associated-items`,
        {
          headers: { 'authentication-token': getJWT()! },
          params: { conciliation_item_ids: itemIds.join(',') },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async updateItem(itemId: number, payload: any): Promise<any> {
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/item/${itemId}`,
        payload,
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async deleteItem(itemId: number): Promise<any> {
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/item/${itemId}`,
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async recalculateStats(conciliationId: number): Promise<any> {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/${conciliationId}/recalculate-stats`,
        {},
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async bulkReconcileItems(itemIds: number[]): Promise<any> {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/items/reconcile`,
        { item_ids: itemIds },
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async createAfterSalesSameReservation(itemIdentifier: string | number, payload: any = {}): Promise<any> {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL_RESERVATION}/item/after-sales/${itemIdentifier}`,
        payload,
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  async createAfterSalesNewReservation(itemIdentifier: string | number, payload: any = {}): Promise<any> {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL_RESERVATION}/item/after-sales/new-reservation/${itemIdentifier}`,
        payload,
        {
          headers: { 'authentication-token': getJWT()! },
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }

  /**
   * Solicita la exportación del detalle de ítems de la conciliación a Excel.
   * El archivo se genera en background y se envía por email.
   * Acepta los mismos filtros que el listado de ítems.
   */
  async exportItemsDetail(
    conciliationId: number | string,
    params: {
      status?: string
      ordering?: string
      code?: string
      is_paid?: boolean
      is_billed?: boolean
      include_deleted?: boolean
    } = {}
  ): Promise<any> {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_RESERVATION}/supplier-conciliation/${conciliationId}/items/export-xlsx`,
        {
          headers: { 'authentication-token': getJWT()! },
          params,
        }
      )
      return res.data
    } catch (error) {
      IsAuthorized(error)
      throw error
    }
  }
}

export const supplierConciliationServices = new SupplierConciliationServices()

